/**
 * Pure mappers: products-api `CombinedProduct` JSON → tidy bench view-models. No I/O.
 * Handles the products-api realities the spec review flagged: `isDefault` is not guaranteed
 * present/unique, the centre stone lives at `ringProduct.stones.centerStones.stones[]` (American
 * spelling, object-with-`.stones[]`), and `carat`/`colourGrade`/`fancyColour` are nullable.
 */
import { enumLabel, isRecorded } from "./enums";

/** Physical measurements in mm. products-api models no table/depth percentage or ratio. */
export interface StoneSize {
  length?: number | undefined;
  width?: number | undefined;
  height?: number | undefined;
}

/**
 * A stone as shown in the bench's pickers and detail sheet. All fields optional because
 * products-api leaves most of them nullable. The bench only *displays* these (the ring-swap API
 * resolves the real attrs from the gemstone id), so this is a view-model, not a wire shape.
 *
 * Deliberately faithful to the wire: products-api stores "unknown" as the value `UNSPECIFIED`
 * (clarity / colourGrade / cut) or `""` (fancyColour / supplierReference), and those survive the
 * mapping so the drawer can sift on them. Guard every *display* with `isRecorded` instead — a
 * mapper that silently rewrote them would make the bench lie about what the API returned.
 *
 * Which attributes a stone can carry depends on `source`: INTERNAL stones never have
 * certification / treatment / origin (products-api has no columns for them), and NIVODA stones
 * always have an empty `colours`. The detail sheet branches on it rather than showing dead rows.
 */
export interface Stone {
  type?: string | undefined;
  shape?: string | undefined;
  carat?: number | undefined;
  clarity?: string | undefined;
  colourGrade?: string | undefined;
  colours?: string[] | undefined;
  fancyColour?: string | undefined;
  size?: StoneSize | undefined;
  quantity?: number | undefined;
  labGrown?: boolean | undefined;
  certified?: boolean | undefined;
  /** INTERNAL (own stock) | NIVODA (live third-party offer). Decides the sections above. */
  source?: string | undefined;
  supplier?: string | undefined;
  supplierReference?: string | undefined;
  certificationLab?: string | undefined;
  certificationNumber?: string | undefined;
  certificateUrl?: string | undefined;
}

/** Build a Cloudinary delivery URL from a stored `providerId` (products-api exposes no URLs). */
export function cloudinaryUrl(providerId: string, cloud: string): string {
  return `https://res.cloudinary.com/${cloud}/image/upload/${providerId}`;
}

/** Cloudinary providerId → URL, but pass an already-absolute image URL (Nivoda S3) through. */
export function resolveImageUrl(providerId: string, cloud: string): string {
  return /^https?:\/\//i.test(providerId) ? providerId : cloudinaryUrl(providerId, cloud);
}

/**
 * Make a stored generated-image URL displayable in this browser. Results are stored as a GCS
 * JSON-API media URL (`…/storage/v1/b/<bucket>/o/<key>?alt=media`). Locally that host is the
 * docker-internal fake-gcs — unreachable from the host browser, and plain-HTTP (mixed content) on
 * this HTTPS page. Rewrite such cross-origin storage URLs to a SAME-ORIGIN path so nginx proxies
 * them to fake-gcs. Prod CDN URLs (different host/path) and same-origin URLs pass through untouched.
 * Display-only; outside a browser (tests/SSR) it is a no-op.
 */
export function toDisplayUrl(url: string | undefined): string | undefined {
  if (!url || typeof window === "undefined") return url;
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin !== window.location.origin && /\/storage\/v1\/b\/[^/]+\/o\//.test(u.pathname)) {
      return `${u.pathname}${u.search}`;
    }
  } catch {
    /* not a parseable URL — leave as-is */
  }
  return url;
}

// ── Loosely-typed views of the external JSON (only the fields we read) ──────────────────────────
interface RawStone {
  type?: string;
  cut?: string; // products-api calls the shape `cut`; ring-swap calls it `shape`
  carat?: number | null;
  clarity?: string;
  colourGrade?: string | null;
  colours?: string[] | null;
  fancyColour?: string | null;
  supplierReference?: string | null; // Nivoda offer id ("DIAMOND/<uuid>") — the addressable stone_sku
  size?: { length?: number | null; width?: number | null; height?: number | null } | null;
  quantity?: number | null;
  labGrown?: boolean | null;
  certified?: boolean | null;
  source?: string | null;
  supplier?: string | null;
  certificationLab?: string | null;
  certificationNumber?: string | null;
  certificateUrl?: string | null;
}
interface RawImage {
  providerId?: string;
  id?: string;
}
interface RawMetal {
  metal?: string;
  isDefault?: boolean;
  castingWeight?: number | null;
}
export interface RawCombinedProduct {
  sku?: string;
  name?: string;
  type?: string;
  audience?: string;
  stockRemaining?: number | null;
  price?: { defaultPrice?: number | null; defaultSettingOnlyPrice?: number | null } | null;
  defaultImageId?: string | null;
  productImages?: RawImage[];
  ringProduct?: {
    size?: string;
    metals?: RawMetal[];
    stones?: {
      centerStones?: { stones?: RawStone[]; talonSet?: boolean };
      accentStones?: { stones?: RawStone[]; totalCarats?: number | null; colouredAccents?: boolean; modelCode?: { code?: string } | null };
    };
  };
  gemstoneProduct?: { stone?: RawStone };
}

/** products-api stone (`cut`) → ring-swap stone view (`shape`); nullable fields → undefined.
 *  `UNSPECIFIED` / `""` are passed through verbatim — see the `Stone` doc comment. */
export function mapStone(raw: RawStone): Stone {
  const stone: Stone = {};
  if (raw.type) stone.type = raw.type;
  if (raw.cut) stone.shape = raw.cut;
  if (typeof raw.carat === "number") stone.carat = raw.carat;
  if (raw.clarity) stone.clarity = raw.clarity;
  if (raw.colourGrade) stone.colourGrade = raw.colourGrade;
  const colours = (raw.colours ?? []).filter((c): c is string => !!c);
  if (colours.length > 0) stone.colours = colours;
  if (raw.fancyColour) stone.fancyColour = raw.fancyColour;
  const size = mapStoneSize(raw.size);
  if (size) stone.size = size;
  if (typeof raw.quantity === "number") stone.quantity = raw.quantity;
  if (typeof raw.labGrown === "boolean") stone.labGrown = raw.labGrown;
  if (typeof raw.certified === "boolean") stone.certified = raw.certified;
  if (raw.source) stone.source = raw.source;
  if (raw.supplier) stone.supplier = raw.supplier;
  if (raw.supplierReference) stone.supplierReference = raw.supplierReference;
  if (raw.certificationLab) stone.certificationLab = raw.certificationLab;
  if (raw.certificationNumber) stone.certificationNumber = raw.certificationNumber;
  if (raw.certificateUrl) stone.certificateUrl = raw.certificateUrl;
  return stone;
}

/** Keep only the mm measurements actually present; drop the object entirely when none are. */
function mapStoneSize(raw: RawStone["size"]): StoneSize | null {
  if (!raw) return null;
  const size: StoneSize = {};
  if (typeof raw.length === "number") size.length = raw.length;
  if (typeof raw.width === "number") size.width = raw.width;
  if (typeof raw.height === "number") size.height = raw.height;
  return Object.keys(size).length > 0 ? size : null;
}

/**
 * A stone's colour is exactly one branch, never both (ring-swap ADR-008): a graded letter
 * (`colourGrade`) for white diamonds, or the fancy branch (`colours[]` / `fancyColour`) for
 * everything else. products-api leaves the unused branch as `UNSPECIFIED` / `[]` / `""` rather than
 * null, so "present" has to mean "recorded". Graded wins when both are somehow set.
 * Returns null when the stone's colour was never recorded at all.
 */
export function colourBranch(stone: Stone): { branch: "graded" | "fancy"; label: string; value: string } | null {
  if (isRecorded(stone.colourGrade)) return { branch: "graded", label: "Colour grade", value: enumLabel(stone.colourGrade as string) };
  const fancy = [...(stone.colours ?? []).map(enumLabel), ...(isRecorded(stone.fancyColour) ? [stone.fancyColour as string] : [])];
  if (fancy.length > 0) return { branch: "fancy", label: "Colour", value: fancy.join(" · ") };
  return null;
}

/**
 * `7.20 × 5.80 × 4.10 mm`, or null when no measurement was recorded.
 *
 * The bare triple is only unambiguous when all three axes are present — dropping an absent one
 * would silently promote the next along ("7.20 × 4.10 mm" reads as length × width when it is really
 * length × height). Partial measurements are labelled instead.
 */
export function formatSize(size: StoneSize | undefined): string | null {
  if (!size) return null;
  const axes: Array<[string, number | undefined]> = [
    ["L", size.length],
    ["W", size.width],
    ["H", size.height],
  ];
  const present = axes.filter((a): a is [string, number] => typeof a[1] === "number");
  if (present.length === 0) return null;
  if (present.length === axes.length) return `${present.map(([, n]) => n.toFixed(2)).join(" × ")} mm`;
  return `${present.map(([axis, n]) => `${axis} ${n.toFixed(2)}`).join(" × ")} mm`;
}

export interface RingImage {
  providerId: string;
  url: string;
  isDefault: boolean;
  /** products-api image ROW id — the value the ring-swap API matches on for `ring_image_id`.
   * Distinct from `providerId` (Cloudinary), which stays the display/URL/UI key. null when absent. */
  productsImageId: string | null;
}

/** A metal the ring is offered in, as catalogued. */
export interface RingMetal {
  metal: string;
  isDefault: boolean;
  castingWeight?: number | undefined;
}

/** The accent-stone summary products-api gives us — a total weight and a descriptive model code,
 *  not individually attributed stones. */
export interface RingAccents {
  totalCarats: number;
  colouredAccents: boolean;
  count: number;
  modelCode?: string | undefined;
}

export interface RingDetail {
  sku: string;
  name: string;
  /** The products-api ProductTypeEnum — includes both wedding-band types, not just rings. */
  productType: string | null;
  audience?: string | undefined;
  price?: number | undefined;
  settingPrice?: number | undefined;
  stockRemaining?: number | undefined;
  /** The ring's made-in metal (its `isDefault` metal, or the first as a fallback). */
  defaultMetal: string | null;
  /** Every metal the ring is offered in — the closest proxy for "allowed swap targets". */
  allowedMetals: string[];
  /** The same metals with their `isDefault` flag and casting weight, for the detail sheet. */
  metals: RingMetal[];
  /** The centre stone the operation baseline is derived from (the first), or null if none. */
  currentStone: Stone | null;
  /** Every centre stone. More than one → `baselineReliable` is false. */
  centreStones: Stone[];
  accents: RingAccents | null;
  /** false when there's no clean single centre stone (multi-stone/custom) → operation warning. */
  baselineReliable: boolean;
  images: RingImage[];
  size?: string | undefined;
  /** Non-fatal mapping warnings to surface in the UI (e.g. no isDefault metal). */
  warnings: string[];
}

/**
 * Map a products-api `CombinedProduct` to the bench's ring view-model. products-api returns the
 * identical shape from the list and detail routes, so this maps both — which is why the catalogue
 * drawer can show a full specimen sheet straight from a list row, with no second fetch.
 */
export function mapRingDetail(raw: RawCombinedProduct, cloud: string): RingDetail {
  const warnings: string[] = [];
  const rawMetals = raw.ringProduct?.metals ?? [];
  const metals: RingMetal[] = rawMetals
    .filter((m): m is RawMetal & { metal: string } => !!m.metal)
    .map((m) => ({
      metal: m.metal,
      isDefault: m.isDefault === true,
      ...(typeof m.castingWeight === "number" ? { castingWeight: m.castingWeight } : {}),
    }));
  const allowedMetals = metals.map((m) => m.metal);

  const explicitDefault = metals.find((m) => m.isDefault)?.metal;
  const defaultMetal = explicitDefault ?? allowedMetals[0] ?? null;
  if (!explicitDefault && defaultMetal) {
    warnings.push("Ring has no isDefault metal in products-api — using the first metal as the baseline.");
  }

  const centre = raw.ringProduct?.stones?.centerStones?.stones ?? [];
  const centreStones = centre.map(mapStone);
  let currentStone: Stone | null = null;
  let baselineReliable = true;
  if (centreStones.length === 1) {
    currentStone = centreStones[0]!;
  } else if (centreStones.length > 1) {
    currentStone = centreStones[0]!;
    baselineReliable = false;
    warnings.push(`Ring has ${centre.length} centre stones — using the first as an approximate baseline.`);
  }

  const images: RingImage[] = (raw.productImages ?? [])
    .filter((i): i is RawImage & { providerId: string } => !!i.providerId)
    .map((i) => ({
      providerId: i.providerId,
      url: cloudinaryUrl(i.providerId, cloud),
      isDefault: i.id !== undefined && i.id === raw.defaultImageId,
      productsImageId: i.id ?? null,
    }));

  const rawAccents = raw.ringProduct?.stones?.accentStones;
  const accents: RingAccents | null = rawAccents
    ? {
        totalCarats: rawAccents.totalCarats ?? 0,
        colouredAccents: rawAccents.colouredAccents === true,
        count: (rawAccents.stones ?? []).length,
        ...(rawAccents.modelCode?.code ? { modelCode: rawAccents.modelCode.code } : {}),
      }
    : null;

  return {
    sku: raw.sku ?? "",
    name: raw.name ?? "",
    productType: raw.type ?? null,
    ...(raw.audience ? { audience: raw.audience } : {}),
    ...(typeof raw.price?.defaultPrice === "number" ? { price: raw.price.defaultPrice } : {}),
    ...(typeof raw.price?.defaultSettingOnlyPrice === "number" ? { settingPrice: raw.price.defaultSettingOnlyPrice } : {}),
    ...(typeof raw.stockRemaining === "number" ? { stockRemaining: raw.stockRemaining } : {}),
    defaultMetal,
    allowedMetals,
    metals,
    currentStone,
    centreStones,
    accents,
    baselineReliable,
    images,
    ...(raw.ringProduct?.size ? { size: raw.ringProduct.size } : {}),
    warnings,
  };
}

export interface GemstoneSummary {
  id: string;
  name: string;
  thumbnailUrl: string | null;
  stone: Stone;
  /** The catalogue SKU. `UJ-…` is own stock; `UJ-TP-…` is a third-party (Nivoda) offer. */
  sku?: string | undefined;
  price?: number | undefined;
  stockRemaining?: number | undefined;
  /** Every photo, so the detail sheet can show a filmstrip rather than one thumbnail. */
  imageUrls: string[];
}

export function mapGemstone(raw: RawCombinedProduct & { id?: string }, cloud: string): GemstoneSummary {
  const imageUrls = (raw.productImages ?? [])
    .filter((i): i is RawImage & { providerId: string } => !!i.providerId)
    .map((i) => resolveImageUrl(i.providerId, cloud));
  // A live Nivoda *offer* is addressable only by its supplierReference ("DIAMOND/<uuid>"), so prefer
  // it as the stone_sku. But own-stock gemstones carry supplierReference = "" (empty string, present),
  // and `??` keeps "" — which would collapse every own-stock stone to the same blank id (the picker
  // keys on it, so clicking one selects them all). Treat a blank reference as absent: fall through to
  // the product id (resolved via /combined-product/{id}). `||` skips the empty string; `?.trim()`
  // also catches whitespace-only values.
  const ref = raw.gemstoneProduct?.stone?.supplierReference?.trim();
  return {
    id: ref || raw.id || raw.sku || "",
    name: raw.name ?? "",
    thumbnailUrl: imageUrls[0] ?? null,
    stone: mapStone(raw.gemstoneProduct?.stone ?? {}),
    ...(raw.sku ? { sku: raw.sku } : {}),
    ...(typeof raw.price?.defaultPrice === "number" ? { price: raw.price.defaultPrice } : {}),
    ...(typeof raw.stockRemaining === "number" ? { stockRemaining: raw.stockRemaining } : {}),
    imageUrls,
  };
}
